import * as React from 'react';

type BaseRouteConfig = Record<string, Record<string, unknown> | undefined>;

type BaseNavigationContextType = {
  currentRoute: string;
  goBack: () => void;
  canGoBack: boolean;
};

const BaseNavigationContext = React.createContext<BaseNavigationContextType>(null!);

export const useBaseNavigation = () => {
  const context = React.useContext(BaseNavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export type Route<T extends BaseRouteConfig = BaseRouteConfig, K extends keyof T = keyof T> = {
  name: K;
  resetStackOnNavigate?: boolean;
};

type NavigationContextType<T> = {
  /**
   * Returns the currently active route name.
   */
  currentRoute: keyof T;
  /**
   * Returns the set params for the currently active route.
   */
  params: T[keyof T];
  /**
   * Navigates to the the specified route.
   *
   * @param route - The route name to navigate to.
   * @param params - Optional params to pass to the navigated route.
   */
  navigate: <K extends keyof T>(route: K, params?: T[K]) => void;
  /**
   * Navigates to the previous route in the stack.
   */
  goBack: () => void;
  /**
   * Sets the parameters for the currently active route.
   *
   * @remarks - Useful for setting state for back navigation.
   *
   * @param params - Parameters to set.
   */
  setCurrentParams: <K extends keyof T>(params: T[K]) => void;
  /**
   * Flag dictating whether there is a route in the history stack to return to.
   */
  canGoBack: boolean;
};

export const createNavigationProvider = <T extends BaseRouteConfig>() => {
  type RouteName = keyof T;

  const TypedNavigationContext = React.createContext<NavigationContextType<T>>(null!);

  const NavigationProvider = ({
    children,
    initialRoute,
    routes,
  }: React.PropsWithChildren<{
    routes: Route<T>[];
    initialRoute: RouteName;
  }>) => {
    const routeConfig = React.useMemo(() => {
      const config: Record<RouteName, { resetStackOnNavigate: boolean }> = {} as any;
      routes.forEach(({ name, resetStackOnNavigate = false }) => {
        config[name] = { resetStackOnNavigate };
      });
      return config;
    }, [routes]);

    const [history, setHistory] = React.useState([
      {
        route: initialRoute,
        params: {} as T[typeof initialRoute],
      },
    ]);

    const navigate = React.useCallback(
      <K extends RouteName>(route: K, params: T[K]) => {
        if (!routeConfig[route]) {
          console.warn(`Route "${String(route)}" not found in configuration`);
          return;
        }

        setHistory((prev) => {
          if (routeConfig[route].resetStackOnNavigate) {
            return [{ route, params }];
          }
          return [...prev, { route, params }];
        });
      },
      [routeConfig],
    );

    const setCurrentParams = React.useCallback(<K extends RouteName>(params: T[K]) => {
      setHistory((prev) => {
        const currentRoute = prev[prev.length - 1];
        const newHistory = prev.slice(0, -1);
        return [...newHistory, { route: currentRoute.route, params }];
      });
    }, []);

    const goBack = React.useCallback(() => {
      setHistory((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    }, []);

    const current = history[history.length - 1];
    const value = {
      currentRoute: current.route,
      params: current.params,
      navigate,
      goBack,
      canGoBack: history.length > 1,
      setCurrentParams,
    };

    return (
      <BaseNavigationContext.Provider value={value as BaseNavigationContextType}>
        <TypedNavigationContext.Provider value={value}>{children}</TypedNavigationContext.Provider>
      </BaseNavigationContext.Provider>
    );
  };

  const useNavigation = <K extends RouteName = RouteName>() => {
    const context = React.useContext(TypedNavigationContext);
    if (!context) {
      throw new Error('useNavigation must be used within a NavigationProvider');
    }

    return {
      ...context,
      params: (context.params ?? {}) as K extends RouteName ? T[K] : T[RouteName],
      setCurrentParams: context.setCurrentParams as K extends RouteName ? (params: T[K]) => void : <R extends RouteName>(params: T[R]) => void,
    };
  };

  return {
    NavigationProvider,
    useNavigation,
  };
};
